import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, ArrowDownRight, ArrowUpRight, BadgeIndianRupee,
  Banknote, CheckCircle2, CircleDollarSign, Landmark, RefreshCw,
  ReceiptIndianRupee, Route, ShieldCheck, Target, WalletCards,
} from "lucide-react";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Pie,
  PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { getMoneyIntelligence, normalizeMoneyClassifications } from "@/lib/api";
import { toast } from "sonner";

const YEARS = [
  { value: 2026, label: "FY 2026–27" },
  { value: 2025, label: "FY 2025–26" },
  { value: 2024, label: "FY 2024–25" },
  { value: 2023, label: "FY 2023–24" },
  { value: 2022, label: "FY 2022–23" },
  { value: 2021, label: "FY 2021–22" },
  { value: 2020, label: "FY 2020–21" },
];
const SECTIONS = ["Overview", "Income", "Spending", "Cash Flow", "Reconciliation"];
const COLORS = ["#22d3ee", "#38bdf8", "#818cf8", "#a78bfa", "#f59e0b", "#34d399", "#fb7185", "#94a3b8"];
const MONTHS = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];

const money = (value) => {
  const n = Number(value || 0);
  if (Math.abs(n) >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
  if (Math.abs(n) >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
  if (Math.abs(n) >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
  return `₹${Math.round(n).toLocaleString("en-IN")}`;
};
const exactMoney = (value) => `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const axis = (value) => money(value).replace("₹", "");

export default function MoneyIntelligencePage({ initialSection = "Overview", onNavigate }) {
  const [year, setYear] = useState(2026);
  const [activeSection, setActiveSection] = useState(initialSection);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const moneyResult = await getMoneyIntelligence(year);
      setData(moneyResult.data);
    } catch {
      toast.error("Could not load Money Intelligence");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [year]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!data || initialSection === "Overview") return;
    requestAnimationFrame(() => document.getElementById(`money-${initialSection.toLowerCase().replace(" ", "-")}`)?.scrollIntoView({ behavior: "smooth", block: "start" }));
  }, [data, initialSection]);

  const goToSection = (name) => {
    setActiveSection(name);
    document.getElementById(`money-${name.toLowerCase().replace(" ", "-")}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const chartData = useMemo(() => (data?.monthly || []).map((row, index) => ({
    ...row, month: MONTHS[index],
  })), [data]);

  if (loading && !data) {
    return <div className="h-72 flex items-center justify-center"><RefreshCw className="animate-spin text-cyan-400" /></div>;
  }
  if (!data) {
    return <div className="p-8 text-[var(--text-muted)]">Money Intelligence is unavailable.</div>;
  }

  const summary = data.summary || {};
  const coverage = data.coverage || {};
  const mismatchCount = data.quality?.classification_mismatches || 0;

  return (
    <div className="p-4 md:p-6 max-w-[1720px] mx-auto space-y-5" data-testid="money-intelligence-page">
      <section className="relative overflow-hidden rounded-2xl border border-cyan-400/15 bg-gradient-to-br from-cyan-400/[0.08] via-[var(--bg-surface)] to-indigo-500/[0.07] p-5 md:p-7">
        <div className="absolute w-56 h-56 rounded-full bg-cyan-400/10 blur-3xl -right-16 -top-24 pointer-events-none" />
        <div className="relative flex flex-wrap items-start justify-between gap-5">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-cyan-300 font-semibold">
              <CircleDollarSign size={14} /> Household operating system
            </div>
            <h1 className="text-2xl md:text-3xl font-bold mt-2">Money Intelligence</h1>
            <p className="mt-2 text-sm text-[var(--text-secondary)] leading-6">
              Recognised income, statement-led spending, cash flow and planning in one governed view.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={year} onChange={(event) => setYear(Number(event.target.value))}
              className="h-10 rounded-lg bg-[var(--bg-base)] border border-white/10 px-3 text-sm text-white">
              {YEARS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
            <button onClick={load} className="h-10 px-3 rounded-lg border border-white/10 text-sm hover:bg-white/5 flex items-center gap-2">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
        </div>
        <div className="relative mt-5 flex flex-wrap items-center gap-2">
          <CoverageBadge coverage={coverage} />
          <span className="text-xs text-[var(--text-muted)]">{coverage.detail}</span>
        </div>
      </section>

      <section className="grid grid-cols-2 xl:grid-cols-6 gap-3">
        <Kpi title="Recognised income" value={money(summary.income)} detail="Manual income + approved passive income" icon={Banknote} tone="emerald" />
        <Kpi title="Household spending" value={money(summary.expenses)} detail="Statements and cash, net of refunds" icon={ReceiptIndianRupee} tone="rose" />
        <Kpi title="Operating surplus" value={money(summary.operating_surplus ?? summary.surplus)} detail={`${summary.savings_rate_pct || 0}% savings rate · before investing`} icon={WalletCards} tone={summary.surplus >= 0 ? "emerald" : "rose"} />
        <Kpi title="Net investments" value={money(summary.net_investment)} detail={`${money(summary.invested)} invested · ${money(summary.withdrawn)} withdrawn`} icon={Landmark} tone="violet" />
        <Kpi title="After investing" value={money(summary.post_investment_balance)} detail="Operating surplus − net investments" icon={CircleDollarSign} tone={summary.post_investment_balance >= 0 ? "emerald" : "rose"} />
        <Kpi title="Data confidence" value={coverage.confidence?.toUpperCase() || "—"} detail={`${summary.records || 0} approved records · ${summary.active_months || 0} active months`} icon={ShieldCheck} tone="amber" wide />
      </section>

      <div className="sticky top-0 z-20 -mx-1 px-1 flex items-center gap-1 overflow-x-auto border-b border-white/10 bg-[var(--bg-base)]/95 backdrop-blur">
        {SECTIONS.map((name) => (
          <button key={name} onClick={() => goToSection(name)}
            className={`px-4 py-3 text-sm whitespace-nowrap border-b-2 transition-colors ${
              activeSection === name ? "border-cyan-400 text-white" : "border-transparent text-[var(--text-muted)] hover:text-white"
            }`}>
            {name}
            {name === "Reconciliation" && mismatchCount > 0 && (
              <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-400/15 text-amber-300">{mismatchCount}</span>
            )}
          </button>
        ))}
        <button onClick={() => onNavigate?.("family_planning")} className="ml-auto px-4 py-3 text-sm whitespace-nowrap text-violet-300 hover:text-violet-200">
          Planning → Family
        </button>
      </div>

      <section id="money-overview" className="scroll-mt-16 space-y-3">
        <SectionHeading eyebrow="Overview" title="Your operating picture" description="The essential story first: what came in, what went out, what remained and what moved into investments." />
        <Overview data={data} chartData={chartData} />
      </section>
      <section id="money-income" className="scroll-mt-16 space-y-3 pt-4">
        <SectionHeading eyebrow="Income" title="Where money came from" description="Authoritative income sources and realised capital gains, without transfers or investment withdrawals." />
        <BreakdownView title="Recognised income" subtitle={data.quality?.rules?.income} rows={data.income_breakdown} total={summary.income} color="#22d3ee" icon={Banknote} capitalGains={data.capital_gains} />
      </section>
      <section id="money-spending" className="scroll-mt-16 space-y-3 pt-4">
        <SectionHeading eyebrow="Spending" title="Where household money went" description="Statement-backed household spending, grouped so the largest categories are immediately visible." />
        <BreakdownView title="Household spending" subtitle={data.quality?.rules?.expenses} rows={data.expense_breakdown} total={summary.expenses} color="#fb7185" icon={ReceiptIndianRupee} />
      </section>
      <section id="money-cash-flow" className="scroll-mt-16 space-y-3 pt-4">
        <SectionHeading eyebrow="Cash Flow" title="How surplus became liquidity or investments" description="Operating surplus, investment movement and current liquid balances in one sequence." />
        <CashFlow chartData={chartData} summary={summary} liquidity={data.liquidity} />
      </section>
      <section id="money-reconciliation" className="scroll-mt-16 space-y-3 pt-4">
        <SectionHeading eyebrow="Reconciliation" title="What limits confidence" description="Exceptions and classification differences only—kept at the end so they do not interrupt the financial story." />
        <Reconciliation data={data} year={year} reload={load} />
      </section>
    </div>
  );
}

function SectionHeading({ eyebrow, title, description }) {
  return <div className="pt-2">
    <div className="text-[10px] uppercase tracking-[.2em] text-cyan-300">{eyebrow}</div>
    <h2 className="text-xl md:text-2xl font-bold mt-1">{title}</h2>
    <p className="text-sm text-[var(--text-muted)] mt-1 max-w-4xl">{description}</p>
  </div>;
}

function Overview({ data, chartData }) {
  return (
    <div className="grid xl:grid-cols-[1.7fr_1fr] gap-4">
      <Panel title="Monthly operating picture" subtitle="Recognised income, household spending and surplus">
        <ResponsiveContainer width="100%" height={330}>
          <BarChart data={chartData}>
            <CartesianGrid stroke="rgba(255,255,255,.06)" vertical={false} />
            <XAxis dataKey="month" tick={{ fill: "#94a3b8", fontSize: 11 }} />
            <YAxis tickFormatter={axis} tick={{ fill: "#64748b", fontSize: 10 }} width={52} />
            <Tooltip formatter={(value, name) => [exactMoney(value), name]} contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} />
            <Legend wrapperStyle={legendStyle} />
            <Bar dataKey="income" name="Recognised income" fill="#22d3ee" radius={[4, 4, 0, 0]} />
            <Bar dataKey="expenses" name="Household spending" fill="#fb7185" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Panel>
      <Panel title="Income composition" subtitle="Only authoritative sources enter recognised income">
        <Donut rows={data.income_breakdown} total={data.summary.income} />
      </Panel>
      <Panel title="Investment movement" subtitle="Contributions and withdrawals are excluded from household income and spending">
        <ResponsiveContainer width="100%" height={270}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="invested" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#818cf8" stopOpacity={0.45} />
                <stop offset="100%" stopColor="#818cf8" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(255,255,255,.06)" vertical={false} />
            <XAxis dataKey="month" tick={{ fill: "#94a3b8", fontSize: 11 }} />
            <YAxis tickFormatter={axis} tick={{ fill: "#64748b", fontSize: 10 }} width={52} />
            <Tooltip formatter={(value, name) => [exactMoney(value), name]} contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} />
            <Legend wrapperStyle={legendStyle} />
            <Area dataKey="invested" name="Invested" stroke="#818cf8" fill="url(#invested)" />
            <Area dataKey="withdrawn" name="Withdrawn" stroke="#f59e0b" fill="transparent" />
          </AreaChart>
        </ResponsiveContainer>
      </Panel>
      <Panel title="Governance rules" subtitle="The accounting policy behind this view">
        <div className="space-y-3">
          <Rule icon={BadgeIndianRupee} title="Income" text={data.quality?.rules?.income} />
          <Rule icon={ReceiptIndianRupee} title="Spending" text={data.quality?.rules?.expenses} />
          <Rule icon={Route} title="Excluded flows" text={data.quality?.rules?.excluded} />
        </div>
      </Panel>
    </div>
  );
}

function BreakdownView({ title, subtitle, rows = [], total, color, icon: Icon, capitalGains }) {
  return (
    <div className="grid xl:grid-cols-[1fr_1.35fr] gap-4">
      <Panel title={`${title} composition`} subtitle={subtitle}>
        <Donut rows={rows} total={total} primary={color} />
        {capitalGains?.total > 0 && (
          <div className="mt-4 rounded-xl border border-cyan-400/15 bg-cyan-400/[0.05] p-4">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-medium">Total realised capital gains</span>
              <span className="font-mono font-semibold">{exactMoney(capitalGains.total)}</span>
            </div>
            <div className="mt-2 text-xs text-[var(--text-muted)] leading-5">
              {money(capitalGains.unclassified)} unclassified + {money(capitalGains.stcg)} STCG + {money(capitalGains.ltcg)} LTCG.
              These are separate, non-overlapping buckets.
            </div>
          </div>
        )}
      </Panel>
      <Panel title={`Detailed ${title.toLowerCase()}`} subtitle={`${rows.length} categories represented`}>
        <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
          {rows.length ? rows.map((row, index) => (
            <div key={row.name} className="flex items-center gap-3 rounded-lg border border-white/[0.06] p-3 bg-white/[0.015]">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${COLORS[index % COLORS.length]}18`, color: COLORS[index % COLORS.length] }}>
                <Icon size={15} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{row.name}</div>
                <div className="mt-1 h-1 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${Math.max(2, Math.abs(row.amount) / Math.max(1, Math.abs(rows[0]?.amount)) * 100)}%`, background: COLORS[index % COLORS.length] }} />
                </div>
              </div>
              <div className="font-mono text-sm">{exactMoney(row.amount)}</div>
            </div>
          )) : <Empty text="No authoritative records for this financial year." />}
        </div>
      </Panel>
    </div>
  );
}

function CashFlow({ chartData, summary, liquidity }) {
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <FlowCard label="Recognised income" value={summary.income} icon={ArrowUpRight} positive />
        <FlowCard label="Household spending" value={summary.expenses} icon={ArrowDownRight} />
        <FlowCard label="Operating surplus" value={summary.operating_surplus ?? summary.surplus} icon={WalletCards} positive={summary.surplus >= 0} />
        <FlowCard label="After investing" value={summary.post_investment_balance} icon={Landmark} positive={summary.post_investment_balance >= 0} />
      </div>
      <Panel title="Monthly cash allocation" subtitle="Operating surplus is before investing; after-investing balance deducts contributions and adds withdrawals">
        <ResponsiveContainer width="100%" height={390}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="surplus" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#34d399" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#34d399" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(255,255,255,.06)" vertical={false} />
            <XAxis dataKey="month" tick={{ fill: "#94a3b8", fontSize: 11 }} />
            <YAxis tickFormatter={axis} tick={{ fill: "#64748b", fontSize: 10 }} width={56} />
            <Tooltip formatter={(value) => exactMoney(value)} contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} />
            <Legend wrapperStyle={legendStyle} />
            <Area dataKey="surplus" name="Operating surplus" stroke="#34d399" strokeWidth={2} fill="url(#surplus)" />
            <Area dataKey="post_investment_balance" name="After investing" stroke="#a78bfa" strokeWidth={2} fill="transparent" />
          </AreaChart>
        </ResponsiveContainer>
        <div className="mt-3 text-xs text-[var(--text-muted)]">
          After investing = recognised income − household spending − investments + investment withdrawals. Transfers remain excluded.
        </div>
      </Panel>
      {liquidity?.available && <LiquidityPanel liquidity={liquidity} />}
    </div>
  );
}

function LiquidityPanel({ liquidity }) {
  const accounts = liquidity.accounts || [];
  const flexi = liquidity.flexi || {};
  const cards = liquidity.credit_cards || {};
  return (
    <Panel title="Household liquidity and current liabilities" subtitle={`${liquidity.confirmed_accounts || 0} savings accounts and ${cards.confirmed_accounts || 0} credit cards have statement-backed balances`}>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
        <LiquidityMetric label="Gross liquid assets" value={liquidity.gross_liquid_assets ?? liquidity.total_savings} detail="Bank savings + linked liquid FDs" highlight />
        <LiquidityMetric label="Current credit-card dues" value={liquidity.current_card_liabilities} detail={`Latest statements through ${cards.latest_date || "—"}`} warning />
        <LiquidityMetric label="Net available liquidity" value={liquidity.net_available_liquidity ?? liquidity.total_savings} detail="Liquid assets less current card dues" highlight />
      </div>
      {!!cards.accounts?.length && <div className="mt-5">
        <div className="mb-2 text-[10px] uppercase tracking-[.14em] text-[var(--text-muted)]">Credit-card liabilities</div>
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {cards.accounts.map(card => (
            <div key={card.account_id} className="rounded-xl border border-rose-400/10 bg-rose-400/[0.025] p-4">
              <div className="text-sm font-medium">{card.account_name}</div>
              {card.available
                ? <><div className="mt-3 text-lg font-mono text-rose-300">{money(card.amount_due)}</div><div className="mt-1 text-[11px] text-[var(--text-muted)]">Statement due · {card.as_of}</div></>
                : <div className="mt-3 text-xs text-amber-300">No explicit due captured from the latest statement</div>}
            </div>
          ))}
        </div>
      </div>}
      <div className="mt-4 grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {accounts.map(account => (
          <div key={account.account_id} className="rounded-xl border border-white/[0.07] bg-white/[0.015] p-4">
            <div className="text-sm font-medium">{account.account_name}</div>
            <div className="mt-1 text-[10px] uppercase tracking-[.12em] text-[var(--text-muted)]">
              {[account.institution, account.owner].filter(Boolean).join(" · ")}
            </div>
            {account.available ? (
              <>
                <div className="mt-3 text-lg font-mono text-white">{money(account.balance)}</div>
                <div className="mt-1 text-[11px] text-[var(--text-muted)]">Closing balance · {account.as_of}</div>
              </>
            ) : (
              <div className="mt-3 text-xs text-amber-300">Awaiting a statement balance for this FY</div>
            )}
          </div>
        ))}
      </div>
      {flexi.available && <div className="mt-4 grid md:grid-cols-4 gap-3 text-xs">
        <MetricLine label="Opening ICICI flexi reserve" value={money(flexi.opening_flexi)} />
        <MetricLine label="Flexi reserve consumed" value={money(flexi.reserve_drawdown)} />
        <MetricLine label="Reverse sweeps tracked" value={money(flexi.tracked_reverse_sweeps)} />
        <MetricLine label="Closure interest recognised" value={money(flexi.closure_interest_recognised)} />
      </div>}
      <div className="mt-4 rounded-lg border border-white/[0.06] bg-white/[0.015] p-3 text-xs text-[var(--text-muted)] leading-5">
        Bank balances are point-in-time assets, not income. Card dues reduce available liquidity but are not counted again as spending; the underlying purchases already appear in household expenses.
      </div>
    </Panel>
  );
}

function LiquidityMetric({ label, value, detail, highlight, warning }) {
  const tone = warning ? "text-amber-300" : highlight ? "text-cyan-300" : "text-white";
  return (
    <div className="rounded-xl border border-white/[0.07] bg-white/[0.015] p-4">
      <div className="text-[10px] uppercase tracking-[.14em] text-[var(--text-muted)]">{label}</div>
      <div className={`mt-2 text-xl font-mono font-bold ${tone}`}>{money(value)}</div>
      <div className="mt-1 text-[11px] text-[var(--text-muted)]">{detail}</div>
    </div>
  );
}

function Planning({ planning, summary, retirement }) {
  const planSummary = planning?.summary || {};
  const timeline = planning?.household_timeline || [];
  const protectedAssets = retirement?.assets || [];
  const maturityTimeline = retirement?.timeline || [];
  const npsHistory = retirement?.nps_history || [];
  const policies = retirement?.insurance || [];
  return (
    <div className="space-y-4">
      <div className="grid xl:grid-cols-[1fr_1.35fr] gap-4">
        <Panel title="Funding capacity" subtitle="Actual recognised surplus compared with the saved planning profile">
          <div className="space-y-4">
            <MetricLine label="Recognised FY surplus" value={money(summary.surplus)} />
            <MetricLine label="Average monthly surplus" value={money(summary.active_months ? summary.surplus / summary.active_months : 0)} />
            <MetricLine label="Planned monthly outflow" value={money(planSummary.total_monthly_outflow)} />
            <MetricLine label="Liability EMI" value={money(planSummary.total_emi)} />
          </div>
          <div className="mt-5 rounded-xl bg-cyan-400/[0.06] border border-cyan-400/15 p-4 text-sm text-[var(--text-secondary)] leading-6">
            Planning records remain independent. Money Intelligence supplies their actual income and spending baseline.
          </div>
        </Panel>
        <Panel title="Upcoming household commitments" subtitle="Saved goals and planned expenses remain governed by Goals & Planning">
          <div className="space-y-2 max-h-[460px] overflow-y-auto">
            {timeline.length ? timeline.slice(0, 16).map((item, index) => (
              <div key={`${item.name}-${index}`} className="flex items-center gap-3 p-3 rounded-lg border border-white/[0.06]">
                <div className="w-9 h-9 rounded-lg bg-violet-400/10 text-violet-300 flex items-center justify-center"><Target size={16} /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{item.name || item.label || "Planned commitment"}</div>
                  <div className="text-xs text-[var(--text-muted)]">{item.date || item.target_date || item.period || "Scheduled"}</div>
                </div>
                <div className="font-mono text-sm">{money(item.amount || item.required_amount || item.value)}</div>
              </div>
            )) : <Empty text="No planning timeline is currently available." />}
          </div>
        </Panel>
      </div>

      <div className="grid xl:grid-cols-[1.2fr_1fr] gap-4">
        <Panel title="Retirement & protected savings" subtitle={`${money(retirement?.current_total)} across PPF, EPF, NPS and SSY`}>
          <div className="space-y-2">
            {protectedAssets.map((asset) => (
              <div key={asset.id} className="grid grid-cols-[auto_1fr_auto] gap-3 items-center p-3 rounded-lg border border-white/[0.06]">
                <div className="w-9 h-9 rounded-lg bg-emerald-400/10 text-emerald-300 flex items-center justify-center"><ShieldCheck size={16} /></div>
                <div className="min-w-0">
                  <div className="text-sm truncate">{asset.name}</div>
                  <div className="text-xs text-[var(--text-muted)]">
                    {asset.holder || "Household"} · {asset.valuation_date || "valuation date pending"}
                    {asset.xirr_pct != null ? ` · XIRR ${asset.xirr_pct}%` : ""}
                  </div>
                </div>
                <div className="font-mono text-sm">{money(asset.current_value)}</div>
              </div>
            ))}
          </div>
          {!!npsHistory.length && (
            <div className="mt-4 rounded-xl border border-white/[0.06] p-4">
              <div className="text-xs uppercase tracking-[.12em] text-[var(--text-muted)]">NPS since inception</div>
              <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
                <div><div className="text-[var(--text-muted)] text-xs">Current</div><div className="font-mono mt-1">{money(npsHistory.at(-1)?.value)}</div></div>
                <div><div className="text-[var(--text-muted)] text-xs">Contributed</div><div className="font-mono mt-1">{money(npsHistory.at(-1)?.contributions)}</div></div>
                <div><div className="text-[var(--text-muted)] text-xs">Notional gain</div><div className="font-mono mt-1 text-emerald-300">{money(npsHistory.at(-1)?.notional_gain)}</div></div>
              </div>
            </div>
          )}
        </Panel>

        <Panel title="Maturity & exit calendar" subtitle="Confirmed maturities, renewal dates and planning milestones">
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {maturityTimeline.length ? maturityTimeline.map((item, index) => (
              <div key={`${item.name}-${item.date}-${index}`} className="flex gap-3 items-center p-3 rounded-lg border border-white/[0.06]">
                <div className={`w-2 h-2 rounded-full shrink-0 ${item.confirmed ? "bg-cyan-300" : "bg-amber-300"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{item.name}</div>
                  <div className="text-xs text-[var(--text-muted)]">{item.date}{item.detail ? ` · ${item.detail}` : ""}</div>
                </div>
                <div className="font-mono text-xs">{money(item.amount)}</div>
              </div>
            )) : <Empty text="No dated maturities are currently available." />}
          </div>
        </Panel>
      </div>

      {!!policies.length && (
        <Panel title="Insurance & maturity-linked funds" subtitle="Protection is separated from investible value so market-linked policies are not double counted">
          <div className="grid lg:grid-cols-2 gap-3">
            {policies.map((policy) => (
              <div key={policy.id} className="rounded-xl border border-white/[0.06] p-4">
                <div className="flex gap-3">
                  <div className="w-9 h-9 rounded-lg bg-rose-400/10 text-rose-300 flex items-center justify-center"><ShieldCheck size={16} /></div>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{policy.plan_name}</div>
                    <div className="text-xs text-[var(--text-muted)] mt-1">{policy.provider} · {policy.policyholder} · {policy.policy_number}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
                  <MetricLine label="Annual premium" value={money(policy.annual_premium)} />
                  {policy.policy_type === "health" ? (
                    <>
                      <MetricLine label="Base family cover" value={money(policy.base_sum_insured)} />
                      <MetricLine label="Current effective cover" value={money(policy.effective_cover)} />
                      <MetricLine label="Renewal due" value={policy.renewal_date || "—"} />
                    </>
                  ) : policy.policy_type === "ulip" ? (
                    <>
                      <MetricLine label="Life cover" value={money(policy.death_benefit)} />
                      <MetricLine label="Premiums paid" value={money(policy.total_premiums_paid)} />
                      <MetricLine label="Current fund value" value={money(policy.current_fund_value)} />
                      <MetricLine label="Maturity date" value={policy.maturity_date || "—"} />
                    </>
                  ) : (
                    <MetricLine label="Guaranteed death benefit" value={money(policy.death_benefit)} />
                  )}
                  {!!policy.guaranteed_income_amount && (
                    <MetricLine
                      label={`${policy.income_frequency || "Annual"} guaranteed income`}
                      value={money(policy.guaranteed_income_amount)}
                    />
                  )}
                  {!!policy.terminal_benefit && <MetricLine label="Terminal benefit" value={money(policy.terminal_benefit)} />}
                </div>
                {policy.policy_type === "health" && (
                  <div className="mt-3 rounded-lg border border-cyan-400/20 bg-cyan-400/[0.05] p-3 text-xs">
                    <div className="text-cyan-200 font-medium">
                      {(policy.covered_members || []).length} members · {policy.plan_structure} · {policy.variant}
                    </div>
                    <div className="text-[var(--text-muted)] mt-1">
                      {(policy.covered_members || []).map((member) => member.name).join(", ")}
                    </div>
                    <div className="text-[var(--text-muted)] mt-1">
                      {policy.waiting_period_status || "Waiting-period status not recorded"}
                    </div>
                  </div>
                )}
                {policy.policy_type === "ulip" && (
                  <div className="mt-3 rounded-lg border border-violet-400/20 bg-violet-400/[0.06] p-3 text-xs">
                    <div className="text-violet-200 font-medium">
                      {policy.fully_paid ? "Fully paid · no future premium" : "Premiums may remain"} · {policy.fund_name}
                    </div>
                    <div className="text-[var(--text-muted)] mt-1">
                      Latest value as of {policy.fund_value_as_of}. {policy.maturity_benefit_basis}
                    </div>
                  </div>
                )}
                {!!policy.income_start_date && (
                  <div className="mt-3 rounded-lg border border-emerald-400/20 bg-emerald-400/[0.06] p-3 text-xs">
                    <div className="text-emerald-300 font-medium">
                      Income from {policy.income_start_date} to {policy.income_end_date}
                    </div>
                    <div className="text-[var(--text-muted)] mt-1">
                      {policy.income_payout_years} annual payments · {money(policy.guaranteed_income_total)} total income,
                      then {money(policy.terminal_benefit)} terminal benefit with the final payment.
                    </div>
                  </div>
                )}
                {!!policy.rider && (
                  <div className="mt-3 rounded-lg border border-white/[0.06] p-3 text-xs">
                    <div className="font-medium">{policy.rider.name}</div>
                    <div className="text-[var(--text-muted)] mt-1">
                      {money(policy.rider.sum_assured)} cover · {money(policy.rider.annual_premium)} annual rider premium ·
                      ends {policy.rider.end_date}
                    </div>
                    {policy.rider.repricing_note && (
                      <div className="text-amber-300 mt-1">{policy.rider.repricing_note}</div>
                    )}
                  </div>
                )}
                {policy.validation_status === "policy_schedule_required" && (
                  <div className="mt-3 text-xs text-amber-300">Policy schedule required to validate maturity, rider renewability and surrender benefits.</div>
                )}
              </div>
            ))}
          </div>
        </Panel>
      )}
    </div>
  );
}

function Reconciliation({ data, year, reload }) {
  const quality = data.quality || {};
  const [normalizing, setNormalizing] = useState(false);
  const normalize = async () => {
    setNormalizing(true);
    try {
      const result = await normalizeMoneyClassifications(year);
      toast.success(`${result.data.normalized || 0} internal classifications normalized`);
      await reload();
    } catch {
      toast.error("Could not normalize internal classifications");
    } finally {
      setNormalizing(false);
    }
  };
  return (
    <div className="grid xl:grid-cols-[1fr_1.4fr] gap-4">
      <Panel title="Source register" subtitle="Every number remains traceable to its origin">
        <div className="space-y-2">
          {(data.sources || []).map((source) => (
            <div key={source.name} className="flex items-center gap-3 p-3 rounded-lg border border-white/[0.06]">
              <div className={`w-2 h-2 rounded-full ${source.statement_native ? "bg-cyan-400" : "bg-violet-400"}`} />
              <div className="flex-1">
                <div className="text-sm">{source.name}</div>
                <div className="text-xs text-[var(--text-muted)]">{source.records} records</div>
              </div>
              <div className="font-mono text-xs text-[var(--text-secondary)]">{money(source.absolute_value)}</div>
            </div>
          ))}
        </div>
      </Panel>
      <Panel title="Internal classification normalization" subtitle="Approved Type is canonical; this aligns the older importer field without changing your approved data">
        <div className={`rounded-xl p-4 border flex gap-3 ${quality.classification_mismatches ? "border-amber-400/20 bg-amber-400/[0.06]" : "border-emerald-400/20 bg-emerald-400/[0.06]"}`}>
          {quality.classification_mismatches ? <AlertTriangle className="text-amber-300 shrink-0" size={20} /> : <CheckCircle2 className="text-emerald-300 shrink-0" size={20} />}
          <div>
            <div className="font-semibold">
              {quality.classification_mismatches || 0} internal classifications to normalize
            </div>
            <div className="text-xs text-[var(--text-muted)] mt-1">
              {quality.safe_to_normalize || 0} safe automatic fixes · {quality.needs_review || 0} need review.
              Money Intelligence already uses the approved Type.
            </div>
          </div>
          {!!quality.classification_mismatches && (
            <button onClick={normalize} disabled={normalizing}
              className="ml-auto shrink-0 h-9 px-3 rounded-lg bg-amber-300 text-slate-950 text-xs font-semibold disabled:opacity-50">
              {normalizing ? "Normalizing…" : "Normalize safely"}
            </button>
          )}
        </div>
        <div className="mt-3 space-y-2 max-h-[360px] overflow-y-auto">
          {(quality.mismatch_examples || []).map((row) => (
            <div key={row.id} className="grid grid-cols-[80px_1fr_auto] gap-3 items-center p-3 rounded-lg border border-white/[0.06] text-xs">
              <span className="text-[var(--text-muted)]">{row.date}</span>
              <div className="min-w-0">
                <div className="truncate text-sm">{row.description || "Transaction"}</div>
                <div className="text-amber-300 mt-0.5">{row.movement_class || "—"} → {row.category}</div>
              </div>
              <span className="font-mono">{money(row.amount)}</span>
            </div>
          ))}
          {!quality.classification_mismatches && <Empty text="All internal movement classes agree with the approved Types." />}
        </div>
        {quality.capital_losses_excluded_from_spending > 0 && (
          <div className="mt-3 text-xs text-[var(--text-muted)]">
            {money(quality.capital_losses_excluded_from_spending)} of capital losses excluded from household spending.
          </div>
        )}
      </Panel>
    </div>
  );
}

function CoverageBadge({ coverage }) {
  const tones = {
    high: "text-emerald-300 bg-emerald-400/10 border-emerald-400/20",
    medium: "text-amber-300 bg-amber-400/10 border-amber-400/20",
    low: "text-rose-300 bg-rose-400/10 border-rose-400/20",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border ${tones[coverage.confidence] || tones.medium}`}>
      {coverage.confidence === "high" ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />}
      {coverage.label}
    </span>
  );
}

function Kpi({ title, value, detail, icon: Icon, tone, wide }) {
  const tones = {
    cyan: "text-cyan-300 bg-cyan-400/10", rose: "text-rose-300 bg-rose-400/10",
    emerald: "text-emerald-300 bg-emerald-400/10", violet: "text-violet-300 bg-violet-400/10",
    amber: "text-amber-300 bg-amber-400/10",
  };
  return (
    <div data-tone={tone} className={`semantic-card rounded-xl border p-4 ${wide ? "col-span-2 xl:col-span-1" : ""}`}>
      <div className="flex justify-between gap-3">
        <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-muted)]">{title}</div>
        <div className={`semantic-icon w-8 h-8 rounded-lg flex items-center justify-center ${tones[tone]}`}><Icon size={15} /></div>
      </div>
      <div className="semantic-value mt-2 text-xl font-bold font-mono">{value}</div>
      <div className="mt-1 text-[11px] text-[var(--text-muted)] leading-4">{detail}</div>
    </div>
  );
}

function Panel({ title, subtitle, children }) {
  return (
    <section className="rounded-xl border border-white/[0.07] bg-[var(--bg-surface)] p-4 md:p-5">
      <h2 className="font-semibold">{title}</h2>
      {subtitle && <p className="text-xs text-[var(--text-muted)] mt-1 mb-4">{subtitle}</p>}
      {children}
    </section>
  );
}

function Donut({ rows = [], total, primary }) {
  const visible = rows.slice(0, 8);
  if (!visible.length) return <Empty text="No data for this period." />;
  return (
    <div className="relative">
      <ResponsiveContainer width="100%" height={260}>
        <PieChart>
          <Pie data={visible} dataKey="amount" nameKey="name" innerRadius={67} outerRadius={100} paddingAngle={2}>
            {visible.map((_, index) => <Cell key={index} fill={index === 0 && primary ? primary : COLORS[index % COLORS.length]} />)}
          </Pie>
          <Tooltip formatter={(value, name) => [exactMoney(value), name]} contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} />
        </PieChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
        <div className="text-center"><div className="text-[10px] uppercase text-[var(--text-muted)]">Total</div><div className="font-mono font-bold">{money(total)}</div></div>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
        {visible.slice(0, 6).map((row, index) => (
          <div key={row.name} className="flex items-center gap-2 text-xs min-w-0">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: index === 0 && primary ? primary : COLORS[index % COLORS.length] }} />
            <span className="truncate text-[var(--text-secondary)]">{row.name}</span>
            <span className="ml-auto font-mono">{money(row.amount)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Rule({ icon: Icon, title, text }) {
  return (
    <div className="flex gap-3 p-3 rounded-lg border border-white/[0.06] bg-white/[0.015]">
      <div className="w-9 h-9 rounded-lg bg-cyan-400/10 text-cyan-300 flex items-center justify-center shrink-0"><Icon size={16} /></div>
      <div><div className="text-sm font-medium">{title}</div><div className="text-xs text-[var(--text-muted)] mt-1 leading-5">{text}</div></div>
    </div>
  );
}

function FlowCard({ label, value, icon: Icon, positive }) {
  return (
    <div className="rounded-xl border border-white/[0.07] bg-[var(--bg-surface)] p-5 flex items-center gap-4">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${positive ? "bg-emerald-400/10 text-emerald-300" : "bg-rose-400/10 text-rose-300"}`}><Icon size={18} /></div>
      <div><div className="text-xs text-[var(--text-muted)]">{label}</div><div className="font-mono font-bold text-lg mt-1">{money(value)}</div></div>
    </div>
  );
}

function MetricLine({ label, value }) {
  return <div className="flex items-center justify-between border-b border-white/[0.06] pb-3"><span className="text-sm text-[var(--text-secondary)]">{label}</span><span className="font-mono">{value}</span></div>;
}

function Empty({ text }) {
  return <div className="py-10 text-center text-sm text-[var(--text-muted)]">{text}</div>;
}

const tooltipStyle = {
  background: "#0b1220", border: "1px solid rgba(255,255,255,.12)",
  borderRadius: 10, color: "#e2e8f0", fontSize: 12,
};
const tooltipLabelStyle = { color: "#f8fafc", fontWeight: 600 };
const tooltipItemStyle = { color: "#e2e8f0" };
const legendStyle = { color: "#cbd5e1", fontSize: 11 };
